/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import {
  INTAKE_SECTIONS,
  IntakeSubmitSchema,
  type IntakeDraftInput,
  type IntakeSection,
  type IntakeSubmitInput,
} from '@therapy/shared-validators';
import { CheckCircle2 } from 'lucide-react';
import { type SyntheticEvent, useCallback, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { type IntakeView } from '../../lib/api/intake.js';
import { useSaveIntakeDraft, useSubmitIntake } from '../../lib/queries/intake.js';

import { INTAKE_CARD_CLASS, toIntakeDefaults } from './intake-form-utils.js';
import { IntakeSectionFields } from './intake-section.js';
import { IntakeStepNav } from './intake-step-nav.js';

const HINT_CLASS = 'mb-5 text-sm text-slate-400';

/** Numbered section header (Figma intake): navy step badge + section title. */
function SectionHeader({
  index,
  section,
}: {
  readonly index: number;
  readonly section: IntakeSection;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="mb-4 flex items-center gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] text-sm font-bold text-white">
        {index}
      </span>
      <h2 id={`intake-${section}`} className="text-lg font-semibold text-slate-800 dark:text-white">
        {t(`intake.section.${section}`)}
      </h2>
    </div>
  );
}

/** Read-only review of a submitted intake (parent's edits locked; reused by therapist/admin). */
export function IntakeReadOnlyView({ view }: { readonly view: IntakeView }): JSX.Element {
  const { t } = useTranslation();
  const { control } = useForm<IntakeSubmitInput>({ defaultValues: toIntakeDefaults(view.form) });
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300">
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
        {t('intake.submittedBanner')}
      </div>
      <fieldset disabled className="space-y-6">
        {INTAKE_SECTIONS.map((section, i) => (
          <section
            key={section}
            className={INTAKE_CARD_CLASS}
            aria-labelledby={`intake-${section}`}
          >
            <SectionHeader index={i + 1} section={section} />
            <IntakeSectionFields control={control} section={section} />
          </section>
        ))}
      </fieldset>
    </div>
  );
}

/** Step-by-step intake wizard (one section per step; validated on Next). */
function IntakeWizard({ view }: { readonly view: IntakeView }): JSX.Element {
  const { t } = useTranslation();
  const saveDraft = useSaveIntakeDraft(view.clientId);
  const submit = useSubmitIntake(view.clientId);
  const { control, handleSubmit, getValues, trigger } = useForm<IntakeSubmitInput>({
    resolver: zodResolver(IntakeSubmitSchema),
    defaultValues: toIntakeDefaults(view.form),
  });
  const [step, setStep] = useState(0);
  const section: IntakeSection = INTAKE_SECTIONS[step] ?? 'basicDetails';
  const isLast = step === INTAKE_SECTIONS.length - 1;

  const onSaveDraft = useCallback(() => {
    saveDraft.mutate(getValues() as IntakeDraftInput);
  }, [saveDraft, getValues]);
  const goBack = useCallback(() => {
    // Clear any stale save/submit error so it doesn't linger across steps.
    saveDraft.reset();
    submit.reset();
    setStep((s) => Math.max(0, s - 1));
  }, [saveDraft, submit]);
  const goNext = useCallback(() => {
    void (async () => {
      if (await trigger(section)) {
        saveDraft.reset();
        submit.reset();
        setStep((s) => Math.min(INTAKE_SECTIONS.length - 1, s + 1));
      }
    })();
  }, [trigger, section, saveDraft, submit]);
  const onSubmit = useCallback(
    (values: IntakeSubmitInput): void => {
      submit.mutate(values);
    },
    [submit],
  );
  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      event.preventDefault();
      if (isLast) {
        void handleSubmit(onSubmit)(event);
      } else {
        goNext();
      }
    },
    [isLast, handleSubmit, onSubmit, goNext],
  );

  return (
    <form onSubmit={onFormSubmit} className="space-y-6" noValidate>
      <IntakeStepNav steps={INTAKE_SECTIONS} current={step} />
      <section className={INTAKE_CARD_CLASS} aria-labelledby={`intake-${section}`}>
        <SectionHeader index={step + 1} section={section} />
        <p className={HINT_CLASS}>{t(`intake.sectionHint.${section}`)}</p>
        <IntakeSectionFields control={control} section={section} />
      </section>

      {(saveDraft.isError || submit.isError) && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button type="button" variant="outline" onClick={goBack} disabled={step === 0}>
          {t('intake.back')}
        </Button>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onSaveDraft}
            disabled={saveDraft.isPending}
          >
            {t('intake.saveDraft')}
          </Button>
          {isLast ? (
            <Button type="submit" disabled={submit.isPending}>
              {t('intake.submit')}
            </Button>
          ) : (
            <Button type="button" onClick={goNext}>
              {t('intake.next')}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
}

/** The eight-section intake form (parent): wizard while editable, read-only once submitted. */
export function PatientDetailsForm({ view }: { readonly view: IntakeView }): JSX.Element {
  return view.canEdit ? <IntakeWizard view={view} /> : <IntakeReadOnlyView view={view} />;
}
